import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

export const inputCls =
  "w-full border border-EAECF0 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-focus-color bg-white disabled:bg-nav-head-bg";

export function Section({ title, children, defaultOpen = true, right = null }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-EAECF0">
      <div className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-000000 hover:bg-f3f8ff">
        <button type="button" className="flex items-center gap-1 flex-1 text-left" onClick={() => setOpen(!open)}>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {title}
        </button>
        {right}
      </div>
      {open && <div className="px-3 pb-3 flex flex-col gap-2">{children}</div>}
    </div>
  );
}

export function Field({ label, children, hint, className = "" }) {
  return (
    <label className={`flex flex-col gap-1 text-[11px] text-344054 ${className}`}>
      {label && <span className="font-medium">{label}</span>}
      {children}
      {hint && <span className="text-[10px] text-667085">{hint}</span>}
    </label>
  );
}

export function Grid({ cols = 2, children }) {
  return <div className={`grid gap-2 ${cols === 4 ? "grid-cols-4" : cols === 3 ? "grid-cols-3" : "grid-cols-2"}`}>{children}</div>;
}

// Number input that keeps its own text while typing and commits valid numbers.
export function NumInput({ value, onChange, step = 0.5, min, max, suffix, disabled, placeholder }) {
  const [text, setText] = useState(value === undefined || value === null ? "" : String(value));
  useEffect(() => {
    setText(value === undefined || value === null ? "" : String(+(+value).toFixed(3)));
  }, [value]);
  const commit = (t) => {
    if (t === "" || t === "-") return;
    let n = parseFloat(t);
    if (isNaN(n)) return;
    if (min !== undefined) n = Math.max(min, n);
    if (max !== undefined) n = Math.min(max, n);
    onChange(n);
  };
  return (
    <div className="relative">
      <input
        type="number"
        className={inputCls + (suffix ? " pr-7" : "")}
        value={text}
        step={step}
        min={min}
        max={max}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value);
          commit(e.target.value);
        }}
      />
      {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-667085 pointer-events-none">{suffix}</span>}
    </div>
  );
}

export function TextInput({ value, onChange, placeholder, disabled, mono }) {
  return (
    <input
      type="text"
      className={inputCls + (mono ? " font-mono" : "")}
      value={value ?? ""}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function Select({ value, onChange, options, disabled }) {
  return (
    <select className={inputCls} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o.value ?? o.id ?? o} value={o.value ?? o.id ?? o}>
          {o.label ?? o}
        </option>
      ))}
    </select>
  );
}

export function ColorInput({ value, onChange, allowEmpty = true }) {
  const v = value || "";
  return (
    <div className="flex items-center gap-1">
      <input
        type="color"
        className="h-6 w-7 border border-EAECF0 rounded cursor-pointer p-0"
        value={/^#[0-9a-f]{6}$/i.test(v) ? v : "#ffffff"}
        onChange={(e) => onChange(e.target.value)}
      />
      <input type="text" className={inputCls + " font-mono"} value={v} placeholder={allowEmpty ? "none" : ""} onChange={(e) => onChange(e.target.value)} />
      {allowEmpty && v && (
        <button type="button" className="text-[10px] text-667085 hover:text-F51D00 px-1" onClick={() => onChange("")} title="Clear">
          ✕
        </button>
      )}
    </div>
  );
}

export function Check({ checked, onChange, label, disabled }) {
  return (
    <label className={`flex items-center gap-2 text-[11px] text-344054 select-none ${disabled ? "opacity-50" : "cursor-pointer"}`}>
      <input type="checkbox" className="accent-primary" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

// Row of icon toggle buttons; value is one option id (or several with `multi`).
export function Segmented({ value, onChange, options, multi }) {
  const isOn = (id) => (multi ? (value || []).includes(id) : value === id);
  return (
    <div className="inline-flex border border-EAECF0 rounded overflow-hidden">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          title={o.title || o.id}
          className={`px-2 py-1 text-xs flex items-center justify-center border-r border-EAECF0 last:border-r-0 ${isOn(o.id) ? "bg-primary text-white" : "bg-white text-344054 hover:bg-f3f8ff"}`}
          onClick={() => {
            if (!multi) return onChange(o.id);
            const cur = value || [];
            onChange(cur.includes(o.id) ? cur.filter((x) => x !== o.id) : [...cur, o.id]);
          }}
        >
          {o.icon || o.label}
        </button>
      ))}
    </div>
  );
}

export function IconBtn({ title, onClick, disabled, active, children, danger, className = "" }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`h-8 min-w-8 px-2 inline-flex items-center justify-center gap-1 rounded text-xs disabled:opacity-40 disabled:cursor-not-allowed ${
        active ? "bg-primary text-white" : danger ? "text-F51D00 hover:bg-F4F4F4" : "text-344054 hover:bg-f3f8ff"
      } ${className}`}
    >
      {children}
    </button>
  );
}

export function Modal({ title, onClose, children, footer, width = "max-w-3xl" }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.4)" }} onMouseDown={onClose}>
      <div className={`bg-white rounded-lg shadow-xl w-full ${width} max-h-[92vh] flex flex-col`} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-EAECF0">
          <h3 className="text-sm font-semibold text-000000">{title}</h3>
          <button type="button" className="text-667085 hover:text-000000 text-lg leading-none" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="flex-1 overflow-auto">{children}</div>
        {footer && <div className="px-4 py-3 border-t border-EAECF0 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export function PrimaryBtn({ children, onClick, disabled, loading, className = "" }) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      onClick={onClick}
      className={`bg-primary text-white text-xs font-medium px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 ${className}`}
    >
      {loading && <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
      {children}
    </button>
  );
}

export function SecondaryBtn({ children, onClick, disabled, className = "" }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`border border-EAECF0 text-344054 bg-white hover:bg-f3f8ff text-xs font-medium px-4 py-2 rounded-md disabled:opacity-50 inline-flex items-center gap-2 ${className}`}
    >
      {children}
    </button>
  );
}
